import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGGauge, PGPieChart } from '../../components/primitives/PGChart';
import { GROUP, BRANCHES, TRENDS, MONTHS, ISO_EVENTS } from '../../utils/orgData';
import { PG } from '../../utils/theme';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';

const { FiCheckCircle, FiAlertTriangle, FiClock, FiFileText, FiLayers } = FiIcons;

const UPCOMING_AUDITS = [
  { date: '2026-05-14', scope: 'ISO 9001 surveillance', branch: 'Group (Sandton)', tone: 'info' },
  { date: '2026-05-28', scope: 'OEM approval — Toyota', branch: 'Cape Town CBD', tone: 'warn' },
  { date: '2026-06-11', scope: 'Internal — QMS §8.5', branch: 'Durban CBD', tone: 'info' },
];

const severityTone = (s) => (s === 'High' ? 'alert' : s === 'Medium' ? 'warn' : s === 'Low' ? 'info' : 'neutral');

export default function ISOSliver() {
  const feed = useLiveFeed();
  const iso = GROUP.kpis.iso;

  const ncrBySeverity = [
    { name: 'High', value: 3, color: PG.red },
    { name: 'Medium', value: 7, color: PG.amber },
    { name: 'Low', value: 12, color: PG.emerald },
    { name: 'Info', value: 5, color: PG.cyan },
  ];

  const branchNameToId = (name) => {
    const b = BRANCHES.find((x) => x.name === name);
    return b?.id;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="COMPLIANCE SCORE" value={iso.complianceScore} unit="%" icon={FiCheckCircle} accent={PG.emerald} target=">98%" animated="ticker" numericValue={Math.round(iso.complianceScore * 10)} tickerDigits={4} tickerSuffix="/10" />
        <KPICard title="NCR OPEN" value={iso.ncrOpen} icon={FiAlertTriangle} accent={PG.amber} target="<25" />
        <KPICard title="NCR OVERDUE" value={iso.ncrOverdue} icon={FiClock} accent={iso.ncrOverdue > 0 ? PG.red : PG.emerald} target="0" animated="splitflap" flapText={String(iso.ncrOverdue).padStart(2, '0')} flapWidth={2} />
        <KPICard title="CAPA EFFECTIVENESS" value={iso.capaEffectiveness} unit="%" icon={FiLayers} accent={PG.emerald} target=">95%" sparkValues={feed.throughputPerMin} />
        <KPICard title="EVIDENCE COMPLETE" value={iso.evidenceCompleteness} unit="%" icon={FiFileText} accent={PG.cyan} target="100%" />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="NCR_BY_SEVERITY" subtitle="open findings" className="col-span-3">
          <div className="h-full p-2">
            <PGPieChart data={ncrBySeverity} />
          </div>
        </Panel>

        <Panel title="ISO_EVENT_LOG" subtitle={`${ISO_EVENTS.length} entries`} className="col-span-6">
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">ID</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Branch</th>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2">Severity</th>
                  <th className="text-right px-3 py-2">Age</th>
                </tr>
              </thead>
              <tbody>
                {ISO_EVENTS.map((e) => {
                  const bid = branchNameToId(e.branch);
                  const rowCls = `border-t border-white/5 hover:bg-[#D71921]/10 transition-colors ${bid ? 'row-clickable cursor-pointer' : ''}`;
                  return (
                    <tr
                      key={e.id}
                      className={rowCls}
                      onClick={bid ? () => { window.location.hash = `#/sliver/iso/branch/${bid}`; } : undefined}
                    >
                      <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400">{e.id}</td>
                      <td className="px-3 py-2 text-neutral-300">{e.type}</td>
                      <td className="px-3 py-2">
                        {bid ? (
                          <Link
                            to={`/sliver/iso/branch/${bid}`}
                            className="text-white hover:text-[#D71921] font-medium"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            {e.branch}
                          </Link>
                        ) : (
                          <span className="text-neutral-400">{e.branch}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-neutral-300 max-w-[320px] truncate">{e.title}</td>
                      <td className="px-3 py-2"><StatusPill tone={severityTone(e.severity)}>{e.severity}</StatusPill></td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[10px] text-neutral-500">{e.age}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="DOC_CONTROL_STATUS" subtitle="procedures current" className="col-span-3">
          <div className="h-full p-2">
            <PGGauge value={iso.docControlScore} label="DOC CONTROL" />
          </div>
        </Panel>

        <Panel title="12_MO_COMPLIANCE_TREND" subtitle="group rolling" className="col-span-8" livePulse>
          <div className="h-full p-2">
            <PGLineChart
              categories={MONTHS}
              series={[{ name: 'Compliance %', data: TRENDS.groupCompliance, itemStyle: { color: PG.emerald }, lineStyle: { color: PG.emerald, width: 2 } }]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel title="UPCOMING_AUDITS" subtitle="next 60 days" className="col-span-4">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {UPCOMING_AUDITS.map((a, i) => (
              <div key={i} className="flex items-start justify-between px-4 py-3 border-b border-white/5">
                <div>
                  <div className="font-doto text-xl text-white leading-none">{a.date}</div>
                  <div className="text-sm text-neutral-300 mt-1">{a.scope}</div>
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 mt-1">{a.branch}</div>
                </div>
                <StatusPill tone={a.tone}>Scheduled</StatusPill>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
